import { useEffect, useState, useContext } from "react";
import { Link } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import PropTypes from "prop-types";
import Cookies from "js-cookie";
import { useStateMachine } from "little-state-machine";
import usePermissions from "hooks/usePermissions";
import { getRequest } from "api/axiosClient";
import errorToast from "helpers/errorToast";
import Input from "components/atoms/Input/Input";
import NavContext from "components/molecules/Navigation/components/common/NavContext";
import { ReactComponent as SearchIcon } from "assets/images/icons/Search.svg";
import { ReactComponent as ClearSearchIcon } from "assets/images/icons/ClearSearch.svg";
import testIds from "constants/testIds";
import "./AutoSuggestSearch.scss";

const MINIMUM_SEARCH_LENGTH = 2;

function AutoSuggestSearch({
  getSuggestionResult,
  setShowRecent,
  selectedSearch,
  setSelectedSearch,
  setIsLoading,
  setStatus,
}) {
  const { REACT_APP_UNIVERSAL_SEARCH_ENDPOINT } = import.meta.env;
  const { navigationConfig, setNavigationConfig } = useContext(NavContext);
  const methods = useForm({ shouldFocusError: true });
  const intl = useIntl();
  const getLastVisits = Cookies.get("lastVisits") && JSON.parse(Cookies.get("lastVisits"));
  const getLastSearches = Cookies.get("lastSearches") && JSON.parse(Cookies.get("lastSearches"));
  const { permissions } = usePermissions();
  const userPermissions = JSON.parse(permissions)?.permissions;
  const [searchValue, setSearchValue] = useState("");
  const [hasError, setHasError] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [closeVisible, setCloseVisible] = useState(false);
  const [searchShipment, setSearchShipment] = useState(false);
  const [searchQuick, setSearchQuick] = useState(false);
  const [searchQuote, setSearchQuote] = useState(false);
  const [searchInvoice, setSearchInvoice] = useState(false);
  const [lastsearches, setLastsearches] = useState(getLastSearches?.length > 0 ? getLastSearches : []);
  const [lastVisits, setLastVisits] = useState(getLastVisits?.length > 0 ? getLastVisits : []);
  const [manageSidebar, setManageSidebar] = useState(false);
  const [inputFocused, setInputFocused] = useState(false);
  const { state } = useStateMachine({ handlesuggestionExpand });

  useEffect(() => {
    getSuggestionResult(suggestions);
    if (userPermissions.tracking.view_shipments) setSearchShipment(true);
    if (userPermissions.quoting.view_quote_requests) setSearchQuote(true);
    if (userPermissions.quoting.view_quick_requests) setSearchQuick(true);
    if (userPermissions.invoicing.view_invoices) setSearchInvoice(true);
  }, [suggestions, userPermissions]);

  useEffect(() => {
    Cookies.set("lastVisits", JSON.stringify(lastVisits.slice(-5)));
    Cookies.set("lastSearches", JSON.stringify(lastsearches.slice(-5)));
  }, [lastsearches, lastVisits]);

  useEffect(() => {
    if (manageSidebar) {
      setNavigationConfig({ ...navigationConfig, expanded: false, isShown: !navigationConfig.isMobile });
      navigationConfig.handleNavigation(navigationConfig.expanded);
    }
  }, [manageSidebar, suggestions]);

  useEffect(() => {
    setSearchValue(selectedSearch);
  }, [selectedSearch]);

  useEffect(() => {
    const getData = setTimeout(() => {
      if (searchValue.length >= 2) {
        setSuggestions([]);
        setIsLoading(true);
        getRequest(`${REACT_APP_UNIVERSAL_SEARCH_ENDPOINT}${searchValue}`)
          .then((response) => {
            if (response?.status === 200) {
              setStatus(true);
            }
            setIsLoading(false);
            setSuggestions(getSuggestions(response.data, searchValue));
          })
          .catch((error) => {
            errorToast(error);
            setHasError(true);
            setIsLoading(false);
          });
      }
    }, 500);

    return () => clearTimeout(getData);
  }, [searchValue]);

  const handleInputChange = (e) => {
    const { value } = e.target;
    setSearchValue(value);
    if (value === "") {
      setStatus(false);
      setSelectedSearch("");
      setSuggestions([]);
      setCloseVisible(false);
    }
  };

  const handleRecentInput = () => {
    if (!setShowRecent) return;
    setShowRecent(true);
  };

  const handleFocus = () => {
    setInputFocused(true);
    if (!setShowRecent) return;
    if (!state.manageSuggestion) {
      setShowRecent(false);
    } else {
      setShowRecent(true);
    }
  };

  function handlesuggestionExpand(state, payload) {
    return {
      ...state,
      manageSuggestion: payload.manageSuggestion,
    };
  }

  function clearSearch() {
    setInputFocused(false);
    setSuggestions([]);
    setSearchValue("");
    setCloseVisible(false);
    setSelectedSearch("");
    setStatus(false);
  }

  // Teach Autosuggest how to calculate suggestions for any given input value.
  function getSuggestions(results, value) {
    const inputValue = value.trim().toLowerCase();
    const inputLength = inputValue.length;

    setCloseVisible(inputLength > 0);

    if (inputLength <= MINIMUM_SEARCH_LENGTH) return [];

    return results.filter((lang) => {
      const matchesInputValue = (field) => field?.toLowerCase()?.includes(inputValue);

      const commonConditionsKeys = [
        "reference_number",
        "title",
        "location",
        "primary_tracking_number",
        "cost_center",
        "tracking_references",
        "packing_list_description",
        "pickup_address_name",
        "delivery_address_name",
        "pickup_contact_email",
        "delivery_contact_email",
        "custom_field1",
        "custom_field2",
        "custom_field3",
        "custom_longfield1",
        "reason_code",
      ];

      const commonConditions = commonConditionsKeys.some((key) => {
        const value = key in lang ? lang[key] : lang.extra_field_data?.[key];
        return value && matchesInputValue(value);
      });

      const isQuick = lang.display_type === "Quick request";
      const isRequest = lang.display_type === "Request";
      const isShipment = lang.display_type === "Shipment";
      const isInvoice = lang.display_type === "Invoice";

      if (searchQuote && searchShipment) return commonConditions;
      if (searchQuote && isRequest) return commonConditions;
      if (searchQuick && isQuick) return commonConditions;
      if (searchShipment && isShipment) return commonConditions;
      if (searchInvoice && isInvoice) return commonConditions;

      return null;
    });
  }

  const handleRecentData = (suggestion) => {
    if (searchValue) {
      const searched = lastsearches.filter((item) => item.toLowerCase() !== searchValue.toLowerCase());
      setLastsearches([...searched, searchValue.toLowerCase()]);
    }
    if (suggestion) {
      const visited = lastVisits.filter((item) => item.reference_number !== suggestion.reference_number);
      setLastVisits([...visited, suggestion]);
      setSuggestions([]);
      setManageSidebar(true);
      setShowRecent(false);
      setSelectedSearch("");
      setStatus(false);
    }
  };

  const showClearButton = (closeVisible && !inputFocused) || inputFocused;

  function ListItem({ items }) {
    return (
      <ul>
        {items.map((item, index) => (
          <li key={index} className="mt-1 font-b3 text-secondary">
            {item}
          </li>
        ))}
      </ul>
    );
  }

  ListItem.propTypes = {
    items: PropTypes.arrayOf(PropTypes.string).isRequired,
  };

  return (
    <FormProvider {...methods}>
      <div className="d-flex flex-column align-items-start position-relative custom-search-icon">
        <div className="position-relative">
          <SearchIcon style={{ transform: "translateY(-3px)" }} />
          <div className="input-group flex-row flex-nowrap drop-shadow br-5 overflow-hidden">
            <Input
              type="text"
              className="font-b2 border-0 w-100 placeholder-light mt-0 mb-0 recent-search"
              value={searchValue}
              placeholder={intl.formatMessage({
                id: "components.organisms.search.placeholder",
                defaultMessage: "Universal Search ...",
              })}
              onChange={handleInputChange}
              onFocus={handleFocus}
              onBlur={() => setInputFocused(false)}
              onClick={handleRecentInput}
              autoFocus={!navigationConfig?.isMobile}
              id={testIds.UNIVERSAL_SEARCH_RESULTS}
              name="universalSearch"
            />
          </div>

          <button
            className={`bg-transparent text-secondary border-0 position-absolute abs-center ${
              showClearButton ? "d-flex" : "d-none"
            }`}
            style={{ right: "16px", top: "-1px", left: "auto", zIndex: 4 }}
            onClick={clearSearch}
            type="submit"
          >
            <ClearSearchIcon />
          </button>
        </div>

        <div
          className="suggestions-container"
          style={{
            height: "85vh",
            overflowY: "auto",
            paddingBottom: "16px",
            width: "100%",
          }}
        >
          {suggestions?.length > 0 &&
            suggestions.map((suggestion, index) => {
              const { display_type, reference_number, title, extra_field_data, shipment_reference_number } = suggestion;
              const { location, primary_tracking_number, tracking_references, packing_list_description } = extra_field_data;

              const filteredTrackingReference = tracking_references
                ?.split(",")
                .map((tracking) => tracking.trim())
                .filter((tracking) => tracking.toLowerCase().includes(searchValue.toLowerCase()));

              const filteredPackingListDescription = packing_list_description
                ?.split(",")
                .map((item) => item.trim())
                .filter((item) => item.toLowerCase().includes(searchValue.toLowerCase()));

              const renderUrl = () => {
                let url = "";
                if (reference_number.includes("QR")) url = `/shipment/${reference_number}/quick`;
                else if (display_type === "Invoice") url = `/shipment/${shipment_reference_number}/billing`;
                else {
                  url =
                    display_type === "Shipment" ? `/shipment/${reference_number}/tracking` : `/shipment/${reference_number}`;
                }
                return url;
              };

              return (
                <div key={index} className="w-100">
                  <Link
                    className="d-flex flex-column justify-content-center align-items-start br-3 bg-cobalt-light drop-shadow overflow-hidden link-reset text-capitalize"
                    to={renderUrl()}
                    onClick={() => handleRecentData(suggestion)}
                  >
                    <span className="font-b4">{display_type}</span>
                    <span className="mb-1 font-b3">
                      {reference_number} - {title}
                    </span>
                    {primary_tracking_number && <span className="mt-1 font-b3">{primary_tracking_number}</span>}
                    {filteredTrackingReference?.length > 0 && <ListItem items={filteredTrackingReference} />}
                    {filteredPackingListDescription?.length > 0 && <ListItem items={filteredPackingListDescription} />}
                    <span className="mt-1 font-b3">{location}</span>
                  </Link>
                </div>
              );
            })}
        </div>

        {hasError && (
          <p className="text-danger">
            <FormattedMessage
              id="components.organisms.search.errorMsg"
              defaultMessage="An error has occured! Please try again."
            />
          </p>
        )}
      </div>
    </FormProvider>
  );
}

AutoSuggestSearch.propTypes = {
  closeNavigation: PropTypes.func.isRequired,
  getSuggestionResult: PropTypes.func.isRequired,
  setShowRecent: PropTypes.func.isRequired,
  selectedSearch: PropTypes.string.isRequired,
  setSelectedSearch: PropTypes.func.isRequired,
  setIsLoading: PropTypes.func.isRequired,
  setStatus: PropTypes.func.isRequired,
};

export default AutoSuggestSearch;
